import { useState } from 'react';
import { Link } from 'react-router';
import type { HouseholdPersonDto } from '@fewerbags/schema';
import type { AttendeeChoice } from '../../lib/autoplan';

export const chipButton = (active: boolean) =>
  `min-h-11 rounded-full px-3.5 text-sm font-semibold transition-colors ${
    active
      ? 'bg-brand text-white shadow-sm'
      : 'border border-line bg-surface text-brand hover:bg-brand-soft'
  }`;

/**
 * Who eats a meal (D32): the usual people (meal time, else everyone), a
 * choice of household people, plus named guests. Choosing nobody without a
 * guest goes back to "usual" — a meal always has someone eating it.
 */
export function AttendeePicker({
  idPrefix,
  choice,
  people,
  usualLabel,
  onChange,
}: {
  idPrefix: string;
  choice: AttendeeChoice;
  people: readonly HouseholdPersonDto[];
  /** What "usual" means here, e.g. "Everyone" or "Ann, Ben". */
  usualLabel: string;
  onChange: (next: AttendeeChoice) => void;
}) {
  const [guest, setGuest] = useState('');
  const allIds = people.map((p) => p.id);
  const everyone =
    choice.personIds !== null &&
    allIds.length > 0 &&
    allIds.every((id) => choice.personIds!.includes(id));

  function set(personIds: string[] | null, guests = choice.guests) {
    if (personIds !== null && personIds.length === 0 && guests.length === 0) personIds = null;
    onChange({ personIds, guests });
  }

  function toggle(id: string) {
    const current = choice.personIds ?? [];
    set(current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  }

  function addGuest() {
    const name = guest.trim().slice(0, 80);
    if (!name || choice.guests.length >= 30) return;
    set(choice.personIds, [...choice.guests, name]);
    setGuest('');
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Who eats">
        <button
          type="button"
          className={chipButton(choice.personIds === null)}
          aria-pressed={choice.personIds === null}
          onClick={() => set(null)}
        >
          Usual ({usualLabel})
        </button>
        {people.length > 1 ? (
          <button
            type="button"
            className={chipButton(everyone)}
            aria-pressed={everyone}
            onClick={() => set(allIds)}
          >
            Everyone
          </button>
        ) : null}
        {people.map((p) => {
          const on = choice.personIds?.includes(p.id) ?? false;
          return (
            <button
              key={p.id}
              type="button"
              className={chipButton(on)}
              aria-pressed={on}
              onClick={() => toggle(p.id)}
            >
              {p.name}
            </button>
          );
        })}
      </div>
      {people.length === 0 ? (
        <p className="text-sm text-muted">
          No household people yet.{' '}
          <Link to="/settings/household" className="font-semibold text-brand underline">
            Add them
          </Link>{' '}
          to plan for who eats; guests work either way.
        </p>
      ) : null}
      {choice.guests.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Guests">
          {choice.guests.map((g, i) => (
            <li key={`${g}-${i}`}>
              <button
                type="button"
                className="min-h-11 rounded-full border border-line bg-cream px-3.5 text-sm font-medium text-ink hover:border-fail hover:text-fail"
                aria-label={`Remove guest ${g}`}
                onClick={() =>
                  set(
                    choice.personIds,
                    choice.guests.filter((_, j) => j !== i),
                  )
                }
              >
                {g} ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-end gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label htmlFor={`${idPrefix}-guest`} className="text-sm font-medium text-ink">
            Guest name
          </label>
          <input
            id={`${idPrefix}-guest`}
            value={guest}
            maxLength={80}
            onChange={(e) => setGuest(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addGuest();
              }
            }}
            placeholder="Grandma"
            className="min-h-11 w-full rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
          />
        </div>
        <button
          type="button"
          className={chipButton(false)}
          onClick={addGuest}
          disabled={!guest.trim()}
        >
          + guest
        </button>
      </div>
    </div>
  );
}
